/**
 * IT Portal — IT/EN i18n catalogue.
 *
 * The IT Portal is operated by both Italian and non-Italian engineers
 * (LoJack EU IT team includes UK/DE staff). The locale is detected
 * automatically from ``navigator.language`` on first visit and stored
 * in ``localStorage["itPortalLocale"]`` so subsequent visits respect
 * the user's manual override (if they switched it via the header
 * dropdown).
 *
 * Scope: ONLY the IT Portal pages — admin panel, dealer panel and
 * customer wallet have their own i18n stacks and are NOT affected.
 *
 * Adding a key
 * ------------
 * 1. Add it to both ``IT`` and ``EN`` blocks (keep IT/EN structurally
 *    identical — the lint rule below would catch any drift).
 * 2. Consume via ``const { t } = useITPortalI18n();`` then ``t.your_key``.
 */
export const IT_PORTAL_I18N = {
  IT: {
    // Page header
    page_title: "Portale IT",
    page_subtitle: "Pannello di controllo tecnico LoJack Digital Wallet",
    locale_label: "Lingua",

    // Tab labels
    tab_metrics: "Metriche",
    tab_resources: "Risorse",
    tab_s3: "S3 Storage",
    tab_infra: "Infrastruttura",
    tab_security: "Security",
    tab_backup: "Backup",
    tab_apple_wallet: "Apple Wallet",
    tab_health: "Health Center",
    tab_monitor: "AI Monitor",
    tab_communications: "Comunicazioni",

    // Communications tab
    comm_title: "Comunicazioni Cliente",
    comm_subtitle: "Imposta paese-per-paese i canali di comunicazione e i campi obbligatori GDPR.",

    // CountryPolicyWidget
    cp_widget_title: "Policy per Paese",
    cp_widget_subtitle: "Configura canali, OTP firma e campi obbligatori per ciascun mercato.",
    cp_legend_email: "Email",
    cp_legend_sms: "SMS",
    cp_col_country: "Paese",
    cp_col_channels: "Canali",
    cp_col_otp: "OTP Firma",
    cp_col_tax_code: "Cod. fiscale",
    cp_col_birth_date: "Data di nascita",
    cp_col_birth_place: "Luogo di nascita",
    cp_col_active: "Attivo",
    cp_opt_both: "Entrambi",
    cp_opt_email_only: "Solo Email",
    cp_opt_sms_only: "Solo SMS",
    cp_opt_otp_email: "Email",
    cp_opt_otp_sms: "SMS",
    cp_opt_otp_both: "Email + SMS",
    cp_save: "Salva modifiche",
    cp_saving: "Salvataggio…",
    cp_reload: "Ricarica",
    cp_dirty_count: "{n} modifiche in sospeso",
    cp_saved_ok: "Policy salvate con successo",
    cp_save_error: "Errore nel salvataggio",

    // Header user actions
    change_password: "Password",
    logout: "Esci",

    // MetricsTab — Tier banner
    m_service_tier_current: "Service Tier Attuale",
    m_headroom_to_threshold: "Headroom: {n}% alla soglia successiva",
    m_uptime: "Uptime",
    m_consecutive_days: "{n} giorni consecutivi",
    m_usage_range: "Fascia Utilizzo",
    m_current_max_usage: "Utilizzo max attuale: {n}%",
    m_next_threshold: "Prossima soglia: {n}%",
    m_pct_used: "{n}% utilizzato",
    m_tier_base: "Base",
    m_tier_medium: "Medio",
    m_tier_high: "Alto",
    m_tier_very_high: "Molto Alto",

    // MetricsTab — Database card
    m_database: "Database",
    m_size: "Dimensione",
    m_active_connections: "Connessioni attive",
    m_total_customers: "Clienti totali",
    m_signed_contracts: "Contratti firmati",
    m_active_dealers: "Dealer attivi",

    // MetricsTab — Security card
    m_security: "Sicurezza",
    m_tls_active: "1.3 attivo",
    m_ssl_certificate: "Certificato SSL",
    m_valid: "Valido",
    m_waf: "WAF",
    m_active: "Attivo",
    m_encryption_at_rest: "Encryption at Rest",
    m_backup_auto: "Backup automatico",

    // MetricsTab — Services card
    m_services: "Servizi",
    m_application: "Applicazione",
    m_api_backend: "API Backend",
    m_database_status: "Database",
    m_email_resend: "Email (Resend)",
    m_google_wallet_api: "Google Wallet API",
    m_online: "Online",
    m_operational: "Operativo",
    m_connected: "Connesso",

    // MetricsTab — History chart
    m_monthly_trend: "Andamento Mensile",
    m_month: "Mese",
    m_customers: "Clienti",
    m_contracts: "Contratti",
    m_tier_col: "Fascia",
    m_last_update: "Ultimo aggiornamento: {date}",

    // BackupTab
    b_title: "Gestione Backup Database",
    b_last_backup: "Ultimo Backup",
    b_never_done: "Mai eseguito",
    b_today: "Oggi",
    b_days_ago: "{n} giorni fa",
    b_total_backups: "Backup Totali",
    b_manual_email_cloud: "{m} manuali, {e} email, {c} cloud",
    b_state: "Stato",
    b_backup_recommended: "Backup Consigliato",
    b_up_to_date: "Aggiornato",
    b_tab_main: "Esegui Backup",
    b_tab_schedule: "Programmazione",
    b_tab_cloud: "Cloud Storage",
    b_tab_history: "Storico",
    b_main_title: "Esegui Backup Manuale",
    b_main_subtitle: "Scarica una copia completa del database o inviala via email agli amministratori",
    b_downloading: "Download...",
    b_download_backup: "Scarica Backup",
    b_sending: "Invio...",
    b_send_email: "Invia via Email",
    b_uploading: "Upload...",
    b_upload_cloud: "Carica su Cloud",
    b_auto_backup: "Backup Automatico",
    b_auto_backup_help: "I backup programmati vengono inviati automaticamente via email agli amministratori",
    b_frequency: "Frequenza:",
    b_freq_daily: "Giornaliero",
    b_freq_daily_desc: "Ogni giorno",
    b_freq_weekly: "Settimanale",
    b_freq_weekly_desc: "Ogni 7 giorni",
    b_freq_biweekly: "Quindicinale",
    b_freq_biweekly_desc: "Ogni 15 giorni",
    b_freq_monthly: "Mensile",
    b_freq_monthly_desc: "Ogni 30 giorni",
    b_freq_footer: "Email automatica agli admin con file JSON allegato",
    b_cloud_external: "Cloud Storage Esterno",
    b_cloud_external_help: "Configura spazio di archiviazione cloud (AWS S3, Google Cloud, Azure, MinIO)",
    b_provider: "Provider",
    b_minio_label: "MinIO / S3 Compatibile",
    b_endpoint_url: "Endpoint URL",
    b_bucket_name: "Bucket Name *",
    b_access_key: "Access Key *",
    b_secret_key: "Secret Key *",
    b_region: "Region",
    b_saving_config: "Salvataggio...",
    b_save_config: "Salva Configurazione",
    b_test_upload: "Test Upload",
    b_enable_to_configure: "Attiva per configurare il salvataggio automatico su cloud esterno",
    b_history_title: "Storico Backup",
    b_no_backups: "Nessun backup eseguito",
    tvc_title: "TelcaVoIP Cloud (pCloud)",
    tvc_help: "Backup gestito sul cloud TelcaVoIP. Le credenziali sono custodite lato server: qui vedi solo stato e attività.",
    tvc_not_configured: "Token pCloud non configurato sul server. Contattare TelcaVoIP.",
    tvc_last_upload: "Ultimo upload",
    tvc_no_upload: "Nessun upload effettuato",
    tvc_quota: "Spazio pCloud",
    tvc_token: "Token di accesso",
    tvc_token_age: "registrato {n} giorni fa",
    tvc_token_expiring: "in scadenza tra {n} giorni — richiedere rotazione a TelcaVoIP",
    tvc_run_now: "Esegui backup ora",
    tvc_running: "Backup in corso...",
    tvc_last_error: "Errore ultimo upload",
    tvc_toast_on: "Backup TelcaVoIP Cloud attivato",
    tvc_toast_off: "Backup TelcaVoIP Cloud disattivato",
    tvc_toast_run_ok: "Backup caricato su TelcaVoIP Cloud",
    tvc_toast_err: "Operazione non riuscita",
    tvc_history_title: "Cronologia upload (ultimi 10)",
    tvc_history_empty: "Nessun upload registrato",
    tvc_ok: "OK",
    tvc_ko: "Errore",
    tvc_restore: "Restore",
    tvc_restore_title: "Restore guidato",
    tvc_restore_desc: "Il restore scarica il file JSON del backup selezionato. L'import nel database è un'operazione manuale e controllata:",
    tvc_restore_step1: "Scarica il file JSON del backup qui sotto",
    tvc_restore_step2: "Verifica il contenuto (clienti, contratti, dealer)",
    tvc_restore_step3: "Importa nel DB via `mongoimport` o `scripts/restore_backup.py`",
    tvc_restore_warn: "Il restore non sovrascrive mai il database in automatico: operazione troppo distruttiva per essere one-click.",
    tvc_restore_audit_note: "Per audit trail, il download invia una notifica email automatica agli amministratori iscritti agli alert Backup Cloud.",
    tvc_restore_download: "Scarica backup JSON",
    tvc_restore_downloading: "Download in corso...",
    tvc_restore_cancel: "Annulla",
    tvc_restore_ok: "Backup {name} scaricato",
    tvc_restore_err: "Download fallito",
    b_customers_count: "{n} clienti",
    b_history_manual: "Download",
    b_history_email: "Email",
    b_history_cloud: "Cloud",

    // ITPortalPage — Login + auth
    it_login_subtitle: "Accesso riservato al dipartimento IT",
    it_login_email_placeholder: "email@lojack.it",
    it_login_password_placeholder: "Password",
    it_login_verifying: "Verifica...",
    it_login_submit: "Accedi al Portale IT",
    it_err_role_not_allowed: "Accesso riservato al personale IT autorizzato.",
    it_err_invalid_credentials: "Credenziali non valide.",

    // ITPortalPage — Forgot password (self-service reset, calls
    // POST /api/auth/admin/forgot-password — same endpoint as Admin
    // LoginPage; the backend queries admin_users and accepts role=it).
    it_forgot_pw_link: "Password dimenticata?",
    it_forgot_pw_title: "Reset password",
    it_forgot_pw_subtitle: "Riceverai un'email con un link valido 60 minuti per reimpostare la password.",
    it_forgot_pw_submit: "Invia link",
    it_forgot_pw_cancel: "Annulla",
    it_forgot_pw_submitted: "Se l'indirizzo è registrato come account IT/Admin, riceverai a breve un'email con il link per reimpostare la password. Controlla anche la cartella spam.",
    it_forgot_pw_close: "Chiudi",
    it_forgot_pw_service_error: "Errore temporaneo del servizio. Riprova fra qualche minuto.",

    // ITPortalPage — Password change
    it_pw_mismatch: "Le password non coincidono",
    it_pw_too_short: "La password deve avere almeno 6 caratteri",
    it_pw_updated: "Password aggiornata con successo",
    it_pw_update_error: "Errore nel cambio password",

    // VA labels + toasts
    va_status_passed: "Superato",
    va_status_warning: "Con Avvisi",
    va_status_critical: "Critico",
    va_register_scan: "Registra Scansione",
    va_last_scan: "Ultima Scansione",
    va_critical_count: "Critiche",
    va_high_count: "Alte",
    va_medium_count: "Medie",
    va_low_count: "Basse",
    va_history: "Storico",
    va_no_scans: "Nessuna scansione registrata",
    va_report: "Report",
    va_delete_confirm: "Eliminare?",
    va_deleted: "Eliminato",
    va_generic_error: "Errore",

    // S3 Storage tab
    s3_bucket_configured: "S3 Bucket Configurato",
    s3_bucket_not_configured: "S3 Bucket Non Configurato",
    s3_enter_creds: "Inserisci le credenziali AWS S3 per attivare il salvataggio contratti",
    s3_test_btn: "Test",
    s3_conn_ok: "Connessione S3 riuscita!",
    s3_conn_fail: "Connessione fallita",
    s3_config_title: "Configurazione AWS S3",
    s3_bucket_name_label: "Bucket Name *",
    s3_region_label: "Region",
    s3_access_key_label: "Access Key ID *",
    s3_secret_key_label: "Secret Access Key *",
    s3_prefix_label: "Prefix / Path",
    s3_prefix_help: "I file verranno salvati come: prefisso/TELAIO.pdf (es. contracts/WBA12345678901234.pdf)",
    s3_save_btn: "Salva",
    s3_save_ok: "Configurazione S3 salvata",
    s3_save_error: "Errore nel salvataggio",
    s3_history_title: "Storico Upload S3",
    s3_no_uploads: "Nessun upload effettuato",

    // SecurityTab — UI labels (compliance body kept in IT for legal review)
    sec_title: "Security & Compliance Overview",
    sec_subtitle: "LoJack Digital Wallet Platform — Documentazione di Sicurezza",
    sec_classified_doc: "Documento riservato",
    sec_rev_label: "Rev. 1.0",
    sec_pdf_download: "Scarica PDF",
    sec_pdf_generating: "Generazione...",
    sec_pdf_translating: "Traduzione AI...",
    sec_compliance_only_it: "I dettagli di compliance dettagliati sono al momento disponibili solo in italiano. Per le sezioni tradotte usa il pulsante \"Scarica PDF\" con la lingua desiderata.",

    // Infrastructure tab cards
    infra_stack_title: "Stack Tecnologico",
    infra_security_title: "Protocolli Sicurezza",
    infra_services_title: "Stato Servizi",
    infra_orchestration: "Orchestrazione",
    infra_container: "Container",
    infra_rate_limiting: "Rate Limiting",
    infra_cors: "CORS",
    infra_csp: "CSP",
    infra_configured: "Configurato",
    infra_implemented: "Implementato",
    infra_hsts_6m: "Attivo (6 mesi)",
    infra_service_online: "Online",

    // Add VA modal
    va_modal_title: "Registra Assessment",
    va_modal_date: "Data Scansione *",
    va_modal_performed_by: "Eseguito da *",
    va_modal_status: "Esito *",
    va_modal_notes: "Note (opzionale)",
    va_modal_report_url: "URL Report (opzionale)",
    va_modal_report_file: "File Report (opzionale)",
    va_modal_save: "Salva",
    va_modal_saving: "Salvataggio...",
    va_modal_cancel: "Annulla",
    va_modal_pass: "Passato",
    va_modal_warning: "Con Avvisi",
    va_modal_critical: "Critico",

    // Change password modal
    pw_modal_title: "Cambia Password",
    pw_current: "Password attuale",
    pw_new: "Nuova password",
    pw_confirm: "Conferma nuova password",
    pw_save: "Aggiorna",
    pw_saving: "Aggiornamento...",
    pw_cancel: "Annulla",

    // Backup toasts
    toast_backup_downloaded: "Backup scaricato con successo",
    toast_backup_download_err: "Errore nel download del backup",
    toast_backup_sent: "Backup inviato a {email}",
    toast_backup_send_err: "Errore nell'invio del backup via email",
    toast_auto_backup_on: "Backup automatico attivato (ogni {n} giorni)",
    toast_auto_backup_off: "Backup automatico disattivato",
    toast_backup_cfg_err: "Errore nella configurazione del backup",
    toast_backup_interval_label_1: "giornaliero",
    toast_backup_interval_label_7: "settimanale",
    toast_backup_interval_label_15: "quindicinale",
    toast_backup_interval_label_30: "mensile",
    toast_auto_backup_set: "Backup automatico {label}",
    toast_backup_interval_err: "Errore nella configurazione",
    toast_cloud_saved: "Configurazione cloud salvata",
    toast_cloud_save_err: "Errore nel salvataggio",
    toast_cloud_upload_err: "Errore upload cloud",
    toast_pdf_err: "Errore nella generazione del PDF",
    toast_va_registered: "Assessment registrato",

    // MonitorTab — AI Operations Monitor
    mon_title: "AI Operations Monitor",
    mon_total_ops: "Operazioni AI Totali",
    mon_cost_saved: "Costo Risparmiato",
    mon_uptime: "Uptime",
    mon_engine_status: "Stato Engine",
    mon_ops_detail: "Dettaglio Operazioni",
    mon_base_ops: "operazioni base",
    mon_description: "Il sistema AI monitora automaticamente le operazioni della piattaforma, gestendo validazione dati, generazione card digitali, invio comunicazioni e analisi delle performance dei dealer. I dati mostrati sono basati sulle metriche aggregate della piattaforma.",

    // HealthCenterTab
    hc_title: "Health Center",
    hc_subtitle: "Stato proattivo di tutte le dipendenze critiche del sistema. Ogni riga ti dice quanto manca alla prossima scadenza, qual è l'ultima verifica e quando ne verrà fatta una nuova. Le card rosse e ambra richiedono azione.",
    hc_refresh_all: "Ricontrolla tutto",
    hc_overall_status: "Stato complessivo",
    hc_last_snapshot: "Ultimo snapshot",
    hc_other_category: "Altri",
    hc_run_now: "Esegui ora",
    hc_err_prefix: "Errore",
    // severity labels
    hc_sev_ok: "Sano",
    hc_sev_info: "Info",
    hc_sev_warn: "Attenzione",
    hc_sev_red: "Urgente",
    hc_sev_unknown: "Sconosciuto",
    hc_sev_not_configured: "Non configurato",
    // overall summary counts
    hc_count_red_one: "urgente",
    hc_count_red_many: "urgenti",
    hc_count_warn: "attenzione",
    hc_count_info: "info",
    hc_count_ok_one: "sano",
    hc_count_ok_many: "sani",
    hc_count_unknown: "sconosciuto",
    hc_count_not_configured_one: "non configurato",
    hc_count_not_configured_many: "non configurati",
    // relative time
    hc_time_in_moment: "fra qualche istante",
    hc_time_sec_ago: "{n}s fa",
    hc_time_min_ago: "{n} minuti fa",
    hc_time_hours_ago: "{n} ore fa",
    hc_time_days_ago: "{n} giorni fa",
    // ETA
    hc_eta_only_ondemand: "Solo on-demand",
    hc_eta_every_min: "Ogni {n} minuti",
    hc_eta_every_hours: "Ogni {n} ore",
    hc_eta_every_days: "Ogni {n} giorni",

    // AppleWalletTab — header, pipeline status, downloads
    aw_title: "Apple Wallet — Pass Type ID Setup",
    aw_subtitle: "Pipeline CSR → .cer Apple → .p12 finale",
    aw_pipeline_status: "Stato pipeline",
    aw_step_csr: "1. CSR generato",
    aw_step_key: "2. Private key disponibile",
    aw_step_p12: "3. .p12 caricato sul server",
    aw_downloads_title: "Download file",
    aw_csr_label: "CSR (per Apple Portal)",
    aw_private_key_label: "Private Key",
    aw_private_key_warn: "SECRET — non condividere via canali pubblici",
    aw_pass_type_id_label: "Pass Type ID",

    // Builder
    aw_builder_title: "Builder Automatico .p12 (consigliato)",
    aw_builder_help: "Carica il .cer firmato da Apple e scegli una password. Il server farà la conversione e ti darà direttamente il .p12 pronto all'uso. Nessuna installazione locale OpenSSL richiesta.",
    aw_builder_cer_label: "File .cer da Apple",
    aw_builder_selected: "Selezionato",
    aw_builder_pwd_label: "Password .p12 (min 6 caratteri)",
    aw_builder_pwd_placeholder: "Scegli una password robusta",
    aw_builder_generating: "Generazione…",
    aw_builder_generate_btn: "Genera & Scarica .p12",

    // Upload .p12 final step
    aw_upload_title: "Step finale — Carica .p12 sul server",
    aw_upload_uploaded_badge: "Caricato",
    aw_upload_already_help: "Il file è già sul server: la piattaforma può generare i pass Apple Wallet. Puoi sostituirlo caricandone uno nuovo, o rimuoverlo con il pulsante cestino.",
    aw_upload_pending_help: "Una volta ottenuto il .p12 (dal builder qui sopra o generato localmente), caricalo qui per completare la pipeline. Il server verifica la password e che la private key dentro il .p12 corrisponda esattamente a quella della CSR generata da questo pannello.",
    aw_upload_file_label: "File .p12",
    aw_upload_pwd_label: "Password del .p12",
    aw_upload_pwd_placeholder: "La password con cui è stato esportato il .p12",
    aw_upload_verifying: "Verifica e salvataggio…",
    aw_upload_replace_btn: "Sostituisci .p12 sul server",
    aw_upload_btn: "Carica .p12 sul server",
    aw_upload_remove_title: "Rimuovi .p12 dal server",
    aw_upload_remove_confirm: "Vuoi davvero rimuovere il .p12 dal server? Dovrai ricaricarlo per riprendere a generare i pass.",

    // Test pass
    aw_test_title: "Test pass su iPhone",
    aw_test_help: "Genera un .pkpass di test (cliente fittizio \"Mario Rossi\", targa TEST123) firmato con il .p12 appena caricato. Scarica il file, inviatelo via AirDrop o e-mail al tuo iPhone e prova ad aprirlo nell'app Wallet: se il pass si aggiunge senza errori, la firma è valida e siamo pronti per la produzione.",
    aw_test_generating: "Generazione test pass…",
    aw_test_btn: "Genera .pkpass di test",
    aw_test_pass_ok: ".pkpass di test generato — aprilo su iPhone per verificare",

    // OpenSSL manual ref
    aw_ssl_title: "Alternativa manuale (OpenSSL su macOS / Linux)",
    aw_ssl_intro: "Se preferisci convertire localmente, scarica entrambi i file (CSR e Private Key) e poi:",
    aw_ssl_step1: "Converti il .cer di Apple in PEM:",
    aw_ssl_step2: "Bundle con la private key:",
    aw_ssl_copy_title: "Copia comando",
    aw_ssl_copied: "Comando copiato negli appunti",

    // Durable storage
    aw_durable_title: "Persistenza durevole (MongoDB)",
    aw_durable_active: "Attiva",
    aw_durable_inactive: "Inattiva",
    aw_durable_none: "Nessun certificato caricato. Quando l'IT manager caricherà il .p12, verrà archiviato in modo durevole su MongoDB e sopravviverà ai redeploy del container.",
    aw_durable_only_db: "Il file .p12 è nel DB ma non ancora nella cache del container. Verrà ripristinato automaticamente alla prossima chiamata Apple Wallet.",
    aw_durable_only_disk: "Il file .p12 è solo nella cache del container (ephemeral): andrà perso al prossimo redeploy! Ricarica il .p12 via il form sotto per attivare la persistenza durevole.",
    aw_durable_ok: "Persistenza durevole attiva: il .p12 è archiviato in MongoDB con la password cifrata Fernet. Sopravviverà ai redeploy.",
    aw_durable_uploaded_by: "Caricato da",
    aw_durable_uploaded_at: "Quando",
    aw_durable_sha256: "SHA-256",
    aw_durable_out_of_sync: "Cache out-of-sync con il DB. Una chiamata Apple Wallet la rigenererà automaticamente.",

    // Sanity test
    aw_sanity_title: "Sanity test pipeline Apple Wallet",
    aw_sanity_help: "Firma in memoria un .pkpass di test e verifica che il certificato sia valido, non scaduto, firmato da Apple WWDR e con il Pass Type ID corretto. Non scrive nulla in produzione, non manda email.",
    aw_sanity_running: "Verifica in corso…",
    aw_sanity_run_btn: "Esegui sanity test",
    aw_sanity_ok: "Pipeline Apple Wallet completamente operativa",
    aw_sanity_warn: "Sanity test ha rilevato problemi — controlla i dettagli sotto",
    aw_sanity_failed: "Sanity test fallito",

    // Cert monitor
    aw_certmon_title: "Monitor scadenza certificato",
    aw_certmon_help: "I certificati Apple Pass Type ID scadono ogni 12 mesi: alla scadenza tutti i pass su iPhone vengono invalidati istantaneamente da iOS senza preavviso. Un cron giornaliero alle 09:00 CET controlla la scadenza e manda email automatiche.",
    aw_certmon_disabled: "Disponibile dopo aver caricato il file .p12.",
    aw_certmon_refresh: "Aggiorna",
    aw_certmon_run_btn: "Esegui ora",
    aw_certmon_force_btn: "Forza alert",
    aw_certmon_force_confirm: "Forzare l'invio dell'alert anche se già notificato di recente?",
    aw_certmon_run_confirm: "Eseguire ora il controllo della scadenza certificato?",
    aw_certmon_status_label: "Stato corrente",
    aw_certmon_expires_on: "Scade il",
    aw_certmon_state_waiting: "In attesa",
    aw_certmon_state_expired: "SCADUTO",
    aw_certmon_state_urgent: "Urgente",
    aw_certmon_state_warning: "Attenzione",
    aw_certmon_state_notice: "Notice",
    aw_certmon_state_healthy: "Sano",
    aw_certmon_days_remaining: "{n} giorni",
    aw_certmon_alerts_sent: "Alert già inviati per questo certificato",
    aw_certmon_no_recipients: "Nessun destinatario configurato. Imposta APPLE_CERT_ALERT_EMAILS nel .env o aggiungi indirizzi a SECURITY_ALERT_EMAILS.",
    aw_certmon_last_check: "Ultimo controllo",
    aw_certmon_days_remaining_short: "{n} giorni rimasti",
    aw_certmon_alert_sent_toast: "Alert \"{threshold}\" inviato a {n} destinatari",
    aw_certmon_healthy_toast: "Certificato in salute — nessun alert necessario ({n} giorni rimasti)",
    aw_certmon_no_active: "Nessun certificato Apple attivo da monitorare",
    aw_certmon_no_alert_needed: "Controllo eseguito — nessun alert",
    aw_certmon_error: "Errore",

    // Common toasts (AppleWallet)
    aw_toast_downloaded: "{name} scaricato",
    aw_toast_download_failed: "Download fallito",
    aw_toast_load_cer_first: "Carica prima il file .cer",
    aw_toast_p12_pwd_short: "Password .p12 troppo corta (min 6 caratteri)",
    aw_toast_p12_generated: "File .p12 generato e scaricato. Conservalo con cura!",
    aw_toast_p12_gen_failed: "Generazione .p12 fallita",
    aw_toast_select_p12: "Seleziona il file .p12 prima",
    aw_toast_enter_p12_pwd: "Inserisci la password del .p12",
    aw_toast_p12_uploaded: ".p12 caricato sul server",
    aw_toast_upload_failed: "Upload fallito",
    aw_toast_p12_removed: "File .p12 rimosso dal server",
    aw_toast_remove_failed: "Rimozione fallita",
    aw_toast_test_failed: "Generazione test fallita",
  },
  EN: {
    // Page header
    page_title: "IT Portal",
    page_subtitle: "LoJack Digital Wallet technical control panel",
    locale_label: "Language",

    // Tab labels
    tab_metrics: "Metrics",
    tab_resources: "Resources",
    tab_s3: "S3 Storage",
    tab_infra: "Infrastructure",
    tab_security: "Security",
    tab_backup: "Backup",
    tab_apple_wallet: "Apple Wallet",
    tab_health: "Health Center",
    tab_monitor: "AI Monitor",
    tab_communications: "Communications",

    // Communications tab
    comm_title: "Customer Communications",
    comm_subtitle: "Set communication channels and GDPR-required fields on a per-country basis.",

    // CountryPolicyWidget
    cp_widget_title: "Per-country Policy",
    cp_widget_subtitle: "Configure channels, signature OTP and required fields for each market.",
    cp_legend_email: "Email",
    cp_legend_sms: "SMS",
    cp_col_country: "Country",
    cp_col_channels: "Channels",
    cp_col_otp: "Signing OTP",
    cp_col_tax_code: "Tax ID",
    cp_col_birth_date: "Date of birth",
    cp_col_birth_place: "Place of birth",
    cp_col_active: "Active",
    cp_opt_both: "Both",
    cp_opt_email_only: "Email only",
    cp_opt_sms_only: "SMS only",
    cp_opt_otp_email: "Email",
    cp_opt_otp_sms: "SMS",
    cp_opt_otp_both: "Email + SMS",
    cp_save: "Save changes",
    cp_saving: "Saving…",
    cp_reload: "Reload",
    cp_dirty_count: "{n} pending changes",
    cp_saved_ok: "Policies saved successfully",
    cp_save_error: "Save failed",

    // Header user actions
    change_password: "Password",
    logout: "Logout",

    // MetricsTab — Tier banner
    m_service_tier_current: "Current Service Tier",
    m_headroom_to_threshold: "Headroom: {n}% to next threshold",
    m_uptime: "Uptime",
    m_consecutive_days: "{n} consecutive days",
    m_usage_range: "Usage Range",
    m_current_max_usage: "Current max usage: {n}%",
    m_next_threshold: "Next threshold: {n}%",
    m_pct_used: "{n}% used",
    m_tier_base: "Base",
    m_tier_medium: "Medium",
    m_tier_high: "High",
    m_tier_very_high: "Very High",

    // MetricsTab — Database card
    m_database: "Database",
    m_size: "Size",
    m_active_connections: "Active connections",
    m_total_customers: "Total customers",
    m_signed_contracts: "Signed contracts",
    m_active_dealers: "Active dealers",

    // MetricsTab — Security card
    m_security: "Security",
    m_tls_active: "1.3 active",
    m_ssl_certificate: "SSL Certificate",
    m_valid: "Valid",
    m_waf: "WAF",
    m_active: "Active",
    m_encryption_at_rest: "Encryption at Rest",
    m_backup_auto: "Automatic backup",

    // MetricsTab — Services card
    m_services: "Services",
    m_application: "Application",
    m_api_backend: "API Backend",
    m_database_status: "Database",
    m_email_resend: "Email (Resend)",
    m_google_wallet_api: "Google Wallet API",
    m_online: "Online",
    m_operational: "Operational",
    m_connected: "Connected",

    // MetricsTab — History chart
    m_monthly_trend: "Monthly Trend",
    m_month: "Month",
    m_customers: "Customers",
    m_contracts: "Contracts",
    m_tier_col: "Tier",
    m_last_update: "Last update: {date}",

    // BackupTab
    b_title: "Database Backup Management",
    b_last_backup: "Last Backup",
    b_never_done: "Never done",
    b_today: "Today",
    b_days_ago: "{n} days ago",
    b_total_backups: "Total Backups",
    b_manual_email_cloud: "{m} manual, {e} email, {c} cloud",
    b_state: "Status",
    b_backup_recommended: "Backup Recommended",
    b_up_to_date: "Up to date",
    b_tab_main: "Run Backup",
    b_tab_schedule: "Schedule",
    b_tab_cloud: "Cloud Storage",
    b_tab_history: "History",
    b_main_title: "Run Manual Backup",
    b_main_subtitle: "Download a full database copy or send it to the administrators via email",
    b_downloading: "Downloading...",
    b_download_backup: "Download Backup",
    b_sending: "Sending...",
    b_send_email: "Send via Email",
    b_uploading: "Uploading...",
    b_upload_cloud: "Upload to Cloud",
    b_auto_backup: "Automatic Backup",
    b_auto_backup_help: "Scheduled backups are automatically emailed to the administrators",
    b_frequency: "Frequency:",
    b_freq_daily: "Daily",
    b_freq_daily_desc: "Every day",
    b_freq_weekly: "Weekly",
    b_freq_weekly_desc: "Every 7 days",
    b_freq_biweekly: "Bi-weekly",
    b_freq_biweekly_desc: "Every 15 days",
    b_freq_monthly: "Monthly",
    b_freq_monthly_desc: "Every 30 days",
    b_freq_footer: "Automatic email to admins with attached JSON file",
    b_cloud_external: "External Cloud Storage",
    b_cloud_external_help: "Configure cloud storage (AWS S3, Google Cloud, Azure, MinIO)",
    b_provider: "Provider",
    b_minio_label: "MinIO / S3 Compatible",
    b_endpoint_url: "Endpoint URL",
    b_bucket_name: "Bucket Name *",
    b_access_key: "Access Key *",
    b_secret_key: "Secret Key *",
    b_region: "Region",
    b_saving_config: "Saving...",
    b_save_config: "Save Configuration",
    b_test_upload: "Test Upload",
    b_enable_to_configure: "Enable to configure automatic external cloud storage",
    b_history_title: "Backup History",
    b_no_backups: "No backups yet",
    tvc_title: "TelcaVoIP Cloud (pCloud)",
    tvc_help: "Managed backup on the TelcaVoIP cloud. Credentials are kept server-side: here you only see status and activity.",
    tvc_not_configured: "pCloud token not configured on the server. Contact TelcaVoIP.",
    tvc_last_upload: "Last upload",
    tvc_no_upload: "No upload yet",
    tvc_quota: "pCloud storage",
    tvc_token: "Access token",
    tvc_token_age: "registered {n} days ago",
    tvc_token_expiring: "expiring in {n} days — request rotation from TelcaVoIP",
    tvc_run_now: "Run backup now",
    tvc_running: "Backup running...",
    tvc_last_error: "Last upload error",
    tvc_toast_on: "TelcaVoIP Cloud backup enabled",
    tvc_toast_off: "TelcaVoIP Cloud backup disabled",
    tvc_toast_run_ok: "Backup uploaded to TelcaVoIP Cloud",
    tvc_toast_err: "Operation failed",
    tvc_history_title: "Upload history (last 10)",
    tvc_history_empty: "No uploads recorded",
    tvc_ok: "OK",
    tvc_ko: "Error",
    tvc_restore: "Restore",
    tvc_restore_title: "Guided restore",
    tvc_restore_desc: "The restore downloads the selected backup's JSON file. Importing it into the database is a manual, controlled operation:",
    tvc_restore_step1: "Download the backup JSON file below",
    tvc_restore_step2: "Verify its contents (customers, contracts, dealers)",
    tvc_restore_step3: "Import into the DB via `mongoimport` or `scripts/restore_backup.py`",
    tvc_restore_warn: "The restore never overwrites the database automatically: that operation is too destructive to be one-click.",
    tvc_restore_audit_note: "For audit trail purposes, the download triggers an automatic email notification to administrators subscribed to Backup Cloud alerts.",
    tvc_restore_download: "Download backup JSON",
    tvc_restore_downloading: "Downloading...",
    tvc_restore_cancel: "Cancel",
    tvc_restore_ok: "Backup {name} downloaded",
    tvc_restore_err: "Download failed",
    b_customers_count: "{n} customers",
    b_history_manual: "Download",
    b_history_email: "Email",
    b_history_cloud: "Cloud",

    // ITPortalPage — Login + auth
    it_login_subtitle: "Restricted access — IT department only",
    it_login_email_placeholder: "email@lojack.it",
    it_login_password_placeholder: "Password",
    it_login_verifying: "Verifying...",
    it_login_submit: "Sign in to IT Portal",
    it_err_role_not_allowed: "Access restricted to authorised IT personnel.",
    it_err_invalid_credentials: "Invalid credentials.",

    // ITPortalPage — Forgot password
    it_forgot_pw_link: "Forgot password?",
    it_forgot_pw_title: "Reset password",
    it_forgot_pw_subtitle: "You will receive an email with a link valid for 60 minutes to reset your password.",
    it_forgot_pw_submit: "Send link",
    it_forgot_pw_cancel: "Cancel",
    it_forgot_pw_submitted: "If the address is registered as an IT/Admin account, you'll shortly receive an email with the reset link. Please also check your spam folder.",
    it_forgot_pw_close: "Close",
    it_forgot_pw_service_error: "Temporary service error. Please retry in a few minutes.",

    // ITPortalPage — Password change
    it_pw_mismatch: "Passwords do not match",
    it_pw_too_short: "Password must be at least 6 characters",
    it_pw_updated: "Password updated successfully",
    it_pw_update_error: "Password change failed",

    // VA labels + toasts
    va_status_passed: "Passed",
    va_status_warning: "Warnings",
    va_status_critical: "Critical",
    va_register_scan: "Register Scan",
    va_last_scan: "Last Scan",
    va_critical_count: "Critical",
    va_high_count: "High",
    va_medium_count: "Medium",
    va_low_count: "Low",
    va_history: "History",
    va_no_scans: "No scans registered",
    va_report: "Report",
    va_delete_confirm: "Delete?",
    va_deleted: "Deleted",
    va_generic_error: "Error",

    // S3 Storage tab
    s3_bucket_configured: "S3 Bucket Configured",
    s3_bucket_not_configured: "S3 Bucket Not Configured",
    s3_enter_creds: "Enter AWS S3 credentials to enable contract storage",
    s3_test_btn: "Test",
    s3_conn_ok: "S3 connection successful!",
    s3_conn_fail: "Connection failed",
    s3_config_title: "AWS S3 Configuration",
    s3_bucket_name_label: "Bucket Name *",
    s3_region_label: "Region",
    s3_access_key_label: "Access Key ID *",
    s3_secret_key_label: "Secret Access Key *",
    s3_prefix_label: "Prefix / Path",
    s3_prefix_help: "Files will be saved as: prefix/VIN.pdf (e.g. contracts/WBA12345678901234.pdf)",
    s3_save_btn: "Save",
    s3_save_ok: "S3 configuration saved",
    s3_save_error: "Save failed",
    s3_history_title: "S3 Upload History",
    s3_no_uploads: "No uploads yet",

    // SecurityTab — UI labels (compliance body kept in IT for legal review)
    sec_title: "Security & Compliance Overview",
    sec_subtitle: "LoJack Digital Wallet Platform — Security Documentation",
    sec_classified_doc: "Confidential document",
    sec_rev_label: "Rev. 1.0",
    sec_pdf_download: "Download PDF",
    sec_pdf_generating: "Generating...",
    sec_pdf_translating: "AI translating...",
    sec_compliance_only_it: "Detailed compliance copy is currently available only in Italian. For translated sections, use the \"Download PDF\" button with your preferred language.",

    // Infrastructure tab cards
    infra_stack_title: "Technology Stack",
    infra_security_title: "Security Protocols",
    infra_services_title: "Service Status",
    infra_orchestration: "Orchestration",
    infra_container: "Container",
    infra_rate_limiting: "Rate Limiting",
    infra_cors: "CORS",
    infra_csp: "CSP",
    infra_configured: "Configured",
    infra_implemented: "Implemented",
    infra_hsts_6m: "Active (6 months)",
    infra_service_online: "Online",

    // Add VA modal
    va_modal_title: "Register Assessment",
    va_modal_date: "Scan Date *",
    va_modal_performed_by: "Performed by *",
    va_modal_status: "Result *",
    va_modal_notes: "Notes (optional)",
    va_modal_report_url: "Report URL (optional)",
    va_modal_report_file: "Report File (optional)",
    va_modal_save: "Save",
    va_modal_saving: "Saving...",
    va_modal_cancel: "Cancel",
    va_modal_pass: "Passed",
    va_modal_warning: "Warnings",
    va_modal_critical: "Critical",

    // Change password modal
    pw_modal_title: "Change Password",
    pw_current: "Current password",
    pw_new: "New password",
    pw_confirm: "Confirm new password",
    pw_save: "Update",
    pw_saving: "Updating...",
    pw_cancel: "Cancel",

    // Backup toasts
    toast_backup_downloaded: "Backup downloaded successfully",
    toast_backup_download_err: "Backup download failed",
    toast_backup_sent: "Backup sent to {email}",
    toast_backup_send_err: "Failed to send backup via email",
    toast_auto_backup_on: "Automatic backup enabled (every {n} days)",
    toast_auto_backup_off: "Automatic backup disabled",
    toast_backup_cfg_err: "Backup configuration failed",
    toast_backup_interval_label_1: "daily",
    toast_backup_interval_label_7: "weekly",
    toast_backup_interval_label_15: "bi-weekly",
    toast_backup_interval_label_30: "monthly",
    toast_auto_backup_set: "Automatic backup {label}",
    toast_backup_interval_err: "Configuration failed",
    toast_cloud_saved: "Cloud configuration saved",
    toast_cloud_save_err: "Save failed",
    toast_cloud_upload_err: "Cloud upload failed",
    toast_pdf_err: "PDF generation failed",
    toast_va_registered: "Assessment registered",

    // MonitorTab — AI Operations Monitor
    mon_title: "AI Operations Monitor",
    mon_total_ops: "Total AI Operations",
    mon_cost_saved: "Cost Saved",
    mon_uptime: "Uptime",
    mon_engine_status: "Engine Status",
    mon_ops_detail: "Operations Detail",
    mon_base_ops: "base operations",
    mon_description: "The AI system automatically monitors platform operations — handling data validation, digital card generation, communications dispatch, and dealer performance analysis. Figures shown are based on the aggregated platform metrics.",

    // HealthCenterTab
    hc_title: "Health Center",
    hc_subtitle: "Proactive status of every critical system dependency. Each row tells you how long until the next expiry, when it was last verified, and when the next check will run. Red and amber cards require action.",
    hc_refresh_all: "Re-check everything",
    hc_overall_status: "Overall status",
    hc_last_snapshot: "Last snapshot",
    hc_other_category: "Other",
    hc_run_now: "Run now",
    hc_err_prefix: "Error",
    hc_sev_ok: "Healthy",
    hc_sev_info: "Info",
    hc_sev_warn: "Warning",
    hc_sev_red: "Urgent",
    hc_sev_unknown: "Unknown",
    hc_sev_not_configured: "Not configured",
    hc_count_red_one: "urgent",
    hc_count_red_many: "urgent",
    hc_count_warn: "warning",
    hc_count_info: "info",
    hc_count_ok_one: "healthy",
    hc_count_ok_many: "healthy",
    hc_count_unknown: "unknown",
    hc_count_not_configured_one: "not configured",
    hc_count_not_configured_many: "not configured",
    hc_time_in_moment: "in a moment",
    hc_time_sec_ago: "{n}s ago",
    hc_time_min_ago: "{n} minutes ago",
    hc_time_hours_ago: "{n} hours ago",
    hc_time_days_ago: "{n} days ago",
    hc_eta_only_ondemand: "On-demand only",
    hc_eta_every_min: "Every {n} minutes",
    hc_eta_every_hours: "Every {n} hours",
    hc_eta_every_days: "Every {n} days",

    // AppleWalletTab — header, pipeline status, downloads
    aw_title: "Apple Wallet — Pass Type ID Setup",
    aw_subtitle: "Pipeline CSR → Apple .cer → final .p12",
    aw_pipeline_status: "Pipeline status",
    aw_step_csr: "1. CSR generated",
    aw_step_key: "2. Private key available",
    aw_step_p12: "3. .p12 uploaded to server",
    aw_downloads_title: "Download files",
    aw_csr_label: "CSR (for Apple Portal)",
    aw_private_key_label: "Private Key",
    aw_private_key_warn: "SECRET — do not share over public channels",
    aw_pass_type_id_label: "Pass Type ID",

    // Builder
    aw_builder_title: "Automatic .p12 Builder (recommended)",
    aw_builder_help: "Upload the .cer signed by Apple and pick a password. The server will perform the conversion and hand you a ready-to-use .p12. No local OpenSSL installation required.",
    aw_builder_cer_label: "Apple .cer file",
    aw_builder_selected: "Selected",
    aw_builder_pwd_label: ".p12 password (min 6 characters)",
    aw_builder_pwd_placeholder: "Choose a strong password",
    aw_builder_generating: "Generating…",
    aw_builder_generate_btn: "Generate & Download .p12",

    // Upload .p12 final step
    aw_upload_title: "Final step — Upload .p12 to server",
    aw_upload_uploaded_badge: "Uploaded",
    aw_upload_already_help: "The file is already on the server: the platform can mint Apple Wallet passes. You can replace it by uploading a new one, or remove it via the trash button.",
    aw_upload_pending_help: "Once you have the .p12 (from the builder above or generated locally), upload it here to complete the pipeline. The server verifies the password and that the private key inside the .p12 matches the one of the CSR generated by this panel.",
    aw_upload_file_label: ".p12 file",
    aw_upload_pwd_label: ".p12 password",
    aw_upload_pwd_placeholder: "The password used to export the .p12",
    aw_upload_verifying: "Verifying and saving…",
    aw_upload_replace_btn: "Replace .p12 on server",
    aw_upload_btn: "Upload .p12 to server",
    aw_upload_remove_title: "Remove .p12 from server",
    aw_upload_remove_confirm: "Are you sure you want to remove the .p12 from the server? You will need to re-upload it to resume minting passes.",

    // Test pass
    aw_test_title: "iPhone pass test",
    aw_test_help: "Generate a test .pkpass (synthetic customer \"Mario Rossi\", licence plate TEST123) signed with the .p12 just uploaded. Download the file, AirDrop or email it to your iPhone, and open it in the Wallet app: if the pass adds without errors, the signature is valid and we are production-ready.",
    aw_test_generating: "Generating test pass…",
    aw_test_btn: "Generate test .pkpass",
    aw_test_pass_ok: "Test .pkpass generated — open it on iPhone to verify",

    // OpenSSL manual ref
    aw_ssl_title: "Manual alternative (OpenSSL on macOS / Linux)",
    aw_ssl_intro: "If you prefer to convert locally, download both files (CSR and Private Key) and then:",
    aw_ssl_step1: "Convert Apple's .cer to PEM:",
    aw_ssl_step2: "Bundle it with the private key:",
    aw_ssl_copy_title: "Copy command",
    aw_ssl_copied: "Command copied to clipboard",

    // Durable storage
    aw_durable_title: "Durable storage (MongoDB)",
    aw_durable_active: "Active",
    aw_durable_inactive: "Inactive",
    aw_durable_none: "No certificate uploaded. When the IT manager uploads the .p12, it will be stored durably in MongoDB and survive container redeploys.",
    aw_durable_only_db: "The .p12 is in the DB but not yet in the container cache. It will be restored automatically on the next Apple Wallet call.",
    aw_durable_only_disk: "The .p12 is only in the container cache (ephemeral): it will be lost on the next redeploy! Re-upload the .p12 via the form below to activate durable storage.",
    aw_durable_ok: "Durable storage active: the .p12 is stored in MongoDB with the password Fernet-encrypted. It will survive redeploys.",
    aw_durable_uploaded_by: "Uploaded by",
    aw_durable_uploaded_at: "When",
    aw_durable_sha256: "SHA-256",
    aw_durable_out_of_sync: "Cache out-of-sync with the DB. An Apple Wallet call will regenerate it automatically.",

    // Sanity test
    aw_sanity_title: "Apple Wallet pipeline sanity test",
    aw_sanity_help: "Signs a test .pkpass in memory and verifies that the certificate is valid, not expired, signed by Apple WWDR and with the correct Pass Type ID. Nothing is written to production, no emails are sent.",
    aw_sanity_running: "Verifying…",
    aw_sanity_run_btn: "Run sanity test",
    aw_sanity_ok: "Apple Wallet pipeline fully operational",
    aw_sanity_warn: "Sanity test detected issues — see details below",
    aw_sanity_failed: "Sanity test failed",

    // Cert monitor
    aw_certmon_title: "Certificate expiry monitor",
    aw_certmon_help: "Apple Pass Type ID certificates expire every 12 months: on expiry every pass on iPhone is instantly invalidated by iOS without notice. A daily cron at 09:00 CET checks the expiry and automatically emails alerts.",
    aw_certmon_disabled: "Available after uploading the .p12 file.",
    aw_certmon_refresh: "Refresh",
    aw_certmon_run_btn: "Run now",
    aw_certmon_force_btn: "Force alert",
    aw_certmon_force_confirm: "Force the alert dispatch even if already notified recently?",
    aw_certmon_run_confirm: "Run the certificate expiry check now?",
    aw_certmon_status_label: "Current status",
    aw_certmon_expires_on: "Expires on",
    aw_certmon_state_waiting: "Waiting",
    aw_certmon_state_expired: "EXPIRED",
    aw_certmon_state_urgent: "Urgent",
    aw_certmon_state_warning: "Warning",
    aw_certmon_state_notice: "Notice",
    aw_certmon_state_healthy: "Healthy",
    aw_certmon_days_remaining: "{n} days",
    aw_certmon_alerts_sent: "Alerts already sent for this certificate",
    aw_certmon_no_recipients: "No recipients configured. Set APPLE_CERT_ALERT_EMAILS in .env or add addresses to SECURITY_ALERT_EMAILS.",
    aw_certmon_last_check: "Last check",
    aw_certmon_days_remaining_short: "{n} days remaining",
    aw_certmon_alert_sent_toast: "Alert \"{threshold}\" sent to {n} recipients",
    aw_certmon_healthy_toast: "Certificate healthy — no alert needed ({n} days remaining)",
    aw_certmon_no_active: "No active Apple certificate to monitor",
    aw_certmon_no_alert_needed: "Check executed — no alert needed",
    aw_certmon_error: "Error",

    // Common toasts (AppleWallet)
    aw_toast_downloaded: "{name} downloaded",
    aw_toast_download_failed: "Download failed",
    aw_toast_load_cer_first: "Upload the .cer file first",
    aw_toast_p12_pwd_short: ".p12 password too short (min 6 characters)",
    aw_toast_p12_generated: ".p12 file generated and downloaded. Keep it safe!",
    aw_toast_p12_gen_failed: ".p12 generation failed",
    aw_toast_select_p12: "Select the .p12 file first",
    aw_toast_enter_p12_pwd: "Enter the .p12 password",
    aw_toast_p12_uploaded: ".p12 uploaded to server",
    aw_toast_upload_failed: "Upload failed",
    aw_toast_p12_removed: ".p12 file removed from server",
    aw_toast_remove_failed: "Remove failed",
    aw_toast_test_failed: "Test generation failed",
  },
};

/**
 * Auto-detect locale on first visit from ``navigator.language``.
 * Returns ``"IT"`` for Italian browsers (``it``, ``it-IT``, ``it-CH``, …)
 * and ``"EN"`` for everything else (fail-safe default).
 *
 * Once a manual selection is made through the header dropdown the
 * value is persisted to ``localStorage`` and wins over auto-detection.
 */
export function detectITPortalLocale() {
  try {
    const stored = window.localStorage.getItem("itPortalLocale");
    if (stored === "IT" || stored === "EN") return stored;
  } catch (_) {
    // localStorage unavailable (private mode, SSR, etc.) — fall through
    // to auto-detection.
  }
  const nav = typeof window !== "undefined" ? window.navigator : null;
  const lang = (nav?.language || nav?.userLanguage || "en").toLowerCase();
  return lang.startsWith("it") ? "IT" : "EN";
}

export function persistITPortalLocale(locale) {
  try {
    window.localStorage.setItem("itPortalLocale", locale);
  } catch (_) {
    // Ignore — non-critical.
  }
}
